import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar } from './Avatar';
import { StarRating, RatingInput } from './StarRating';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewsSection = ({ sellerId, listingId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average_rating: 0, total_reviews: 0 });
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchSummary();
  }, [sellerId]);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/seller/${sellerId}`);
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/seller/${sellerId}/summary`);
      setSummary(data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reviews`,
        { seller_id: sellerId, listing_id: listingId, rating, comment },
        { withCredentials: true }
      );
      toast.success('Review submitted!');
      setShowForm(false);
      setRating(0);
      setComment('');
      fetchReviews();
      fetchSummary();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to submit review';
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const userHasReviewed = user && reviews.some(r => r.reviewer_id === user.id);
  const canReview = user && user.id !== sellerId;

  return (
    <div className="mt-6 p-6 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid="reviews-section">
      <div className="flex flex-wrap justify-between items-start mb-6 gap-4">
        <div>
          <h3 className="text-2xl mb-2 tracking-tight" style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
            Community Reviews
          </h3>
          <div className="flex items-center gap-3">
            <StarRating rating={summary.average_rating} size={20} />
            <span style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }} data-testid="reviews-summary">
              {summary.total_reviews > 0
                ? `${summary.average_rating} out of 5 · ${summary.total_reviews} review${summary.total_reviews !== 1 ? 's' : ''}`
                : 'No reviews yet'}
            </span>
          </div>
        </div>
        {canReview && !showForm && (
          <Button
            onClick={() => {
              setShowForm(true);
              if (userHasReviewed) {
                const existing = reviews.find(r => r.reviewer_id === user.id);
                if (existing) {
                  setRating(existing.rating);
                  setComment(existing.comment);
                }
              }
            }}
            variant="outline"
            className="rounded-md"
            style={{ fontFamily: 'Manrope', borderColor: '#D95A41', color: '#D95A41' }}
            data-testid="write-review-btn"
          >
            {userHasReviewed ? 'Update Review' : 'Write a Review'}
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#F9F6F0' }} data-testid="review-form">
          <div className="mb-4">
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
              Your Rating
            </label>
            <RatingInput value={rating} onChange={setRating} size={28} />
          </div>
          <div className="mb-4">
            <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
              Your Review
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Share your experience with this cook..."
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#FFFFFF' }}
              data-testid="review-comment-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-md"
              style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="submit-review-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
            <Button
              type="button"
              onClick={() => { setShowForm(false); setRating(0); setComment(''); }}
              variant="ghost"
              style={{ fontFamily: 'Manrope' }}
              data-testid="cancel-review-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {reviews.length > 0 ? (
          reviews.map((review, idx) => (
            <div key={review.id} className="pb-4 border-b border-[#E5E0D8] last:border-0" data-testid={`review-${idx}`}>
              <div className="flex items-start gap-3">
                <Avatar name={review.reviewer_name} size={40} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '14px' }}>
                      {review.reviewer_name}
                    </span>
                    <span style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                      {new Date(review.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <StarRating rating={review.rating} size={14} />
                  <p className="mt-2" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#1A1A1A', lineHeight: '1.6' }}>
                    {review.comment}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', textAlign: 'center', padding: '1rem' }}>
            Be the first to review this cook!
          </p>
        )}
      </div>
    </div>
  );
};
